import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Pagination,
  CircularProgress,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { filterArticles } from "../../models/Article";
import ArticleCardList from "../Layout/ArticleCardList";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";

const WAR_DIARY_CATEGORY_ID = 1;

const periods = [
  { label: "Tất cả", value: "" },
  { label: "Kháng chiến chống Pháp", value: "KCCP" },
  { label: "Kháng chiến chống Mỹ", value: "KCCM" },
  { label: "1975 - nay", value: "1975" },
];

const types = [
  { label: "Tất cả", value: "" },
  { label: "Bài biên tập", value: "text" },
  { label: "Video", value: "video" },
];

export default function WarDiarySection() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const defaultTimeline = params.get("timeline") || "";

  const [selectedPeriod, setSelectedPeriod] = useState(defaultTimeline);
  const [selectedType, setSelectedType] = useState("");
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(4);
  const [articles, setArticles] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sortType, setSortType] = useState("desc");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const timelineQuery = params.get("timeline");
    if (timelineQuery) {
      setSelectedPeriod(timelineQuery);
      setPage(1);
    }
  }, [location.search]);

  const getTimelineId = (timelineValue) => {
    switch (timelineValue) {
      case "KCCP":
        return 1;
      case "KCCM":
        return 2;
      case "1975":
        return 3;
      default:
        return undefined;
    }
  };

  const fetchArticles = async () => {
    const timeline_id = getTimelineId(selectedPeriod);
    setLoading(true);

    try {
      const payload = {
        page,
        limit: itemsPerPage,
        category_id: WAR_DIARY_CATEGORY_ID,
        timeline_id,
        sort: sortType,
      };
      if (selectedType) payload.type = selectedType.toLowerCase();

      const res = await filterArticles(payload);
      if (res) {
        setArticles(res.data);
        setTotalPages(Math.ceil(res.total / itemsPerPage));
      } else {
        setArticles([]);
      }
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, [selectedPeriod, selectedType, page, sortType]);

  return (
    <Box sx={{ px: 2, py: 5, maxWidth: 1440, mx: "auto" }}>
      <Paper
        elevation={2}
        sx={{
          maxWidth: 900,
          mx: "auto",
          mb: 5,
          px: 4,
          py: 3,
          borderRadius: 2,
          backgroundColor: "#faf7f1",
        }}
      >
        <Box display="flex" justifyContent="center" mb={1}>
          <FormatQuoteIcon sx={{ fontSize: 40, opacity: 0.3 }} />
        </Box>
        <Typography
          variant="h6"
          align="center"
          fontStyle="italic"
          sx={{ lineHeight: 1.6 }}
        >
          <Box component="span" fontWeight="bold" fontStyle="normal">
            Chiến Ký
          </Box>{" "}
          là nơi những ký ức về một thời mưa bom bão đạn cùng những tình cảm thiêng liêng đầy 
trân quý được tái hiện đầy chân thực, sống động bởi chính những người lính đã vào sinh 
ra tử nơi chiến trường cam go, khốc liệt. Sau cùng là lời nhắn gửi đầy tâm tình từ thế hệ 
cha ông đến thế hệ tương lai trong sự nghiệp bảo vệ nền hoà bình, độc lập dân tộc.  
        </Typography>
      </Paper>

      <Grid container spacing={4} justifyContent={{ xs: "center", md: "flex-start" }}> 
        {/* Bộ lọc bên trái */}
        <Grid item xs={12} md={3}>
          <Paper elevation={3} sx={{ p: 2, position: "sticky", top: 100, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Giai đoạn
            </Typography>
            <List>
              {periods.map((p) => (
                <ListItem disablePadding key={p.value}>
                  <ListItemButton
                    selected={selectedPeriod === p.value}
                    onClick={() => {
                      setSelectedPeriod(p.value);
                      setPage(1);
                    }}
                  >
                    <ListItemText primary={p.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>

            <Typography variant="h6" fontWeight={600} mt={4} mb={2}>
              Loại nội dung
            </Typography>
            <List>
              {types.map((t) => (
                <ListItem disablePadding key={t.value}>
                  <ListItemButton
                    selected={selectedType === t.value}
                    onClick={() => {
                      setSelectedType(t.value);
                      setPage(1);
                    }}
                  >
                    <ListItemText primary={t.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>

            <Typography variant="h6" fontWeight={600} mt={4} mb={2}>
              Lọc theo thời gian
            </Typography>
            <List>
              <ListItem disablePadding>
                <ListItemButton
                  selected={sortType === "desc"}
                  onClick={() => {
                    setSortType("desc");
                    setPage(1);
                  }}
                >
                  <ListItemText primary="Mới nhất → Cũ nhất" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  selected={sortType === "asc"}
                  onClick={() => {
                    setSortType("asc");
                    setPage(1);
                  }}
                >
                  <ListItemText primary="Cũ nhất → Mới nhất" />
                </ListItemButton>
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Danh sách bài viết */}
        <Grid item xs={12} md={9} justifyContent={'center'}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <ArticleCardList articles={articles} />
              <Box display="flex" justifyContent="center" mt={4}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, val) => setPage(val)}
                  color="primary"
                />
              </Box>
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
